import { useEffect, useState } from 'react';
import { AutocompleteField } from '@/components/common';
import { searchPlaces, type PlaceSearchResult } from '@/utils/geo';
import type { SimpleTextFieldProps } from './RadixProps';

const SEARCH_DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 3;

export interface LocationSearchFieldProps extends Omit<SimpleTextFieldProps, 'value' | 'onChange'> {
  /** Called with the picked place; the field clears itself afterwards. */
  onPlaceSelect: (place: PlaceSearchResult) => void;
}

/**
 * Place-search typeahead backed by OpenStreetMap Nominatim. Manages its own
 * query text — the parent only receives the selected place via `onPlaceSelect`.
 */
export function LocationSearchField({
  onPlaceSelect,
  onBlur,
  onFocus,
  defaultValue,
  ...rest
}: LocationSearchFieldProps) {
  const [query, setQuery] = useState(() => String(defaultValue ?? ''));
  const [search, setSearch] = useState<{ query: string; places: PlaceSearchResult[] } | null>(null);

  const trimmed = query.trim();
  const searching = trimmed.length >= MIN_QUERY_LENGTH && search?.query !== trimmed;
  const suggestions = search?.query === trimmed ? search.places : [];

  useEffect(() => {
    // Skip when results (or a deliberate empty state after selection) already
    // cover the current query — also prevents the label written on select from
    // triggering a new search for itself.
    if (trimmed.length < MIN_QUERY_LENGTH || trimmed === search?.query) return;

    let cancelled = false;
    const timer = setTimeout(() => {
      void searchPlaces(trimmed).then(places => {
        if (!cancelled) setSearch({ query: trimmed, places });
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [trimmed, search?.query]);

  return (
    <AutocompleteField
      {...rest}
      value={query}
      autoComplete="off"
      loading={searching}
      selectOnEnter
      onChange={setQuery}
      onBlur={onBlur}
      onFocus={onFocus}
      options={suggestions.map((place, index) => ({ id: String(index), label: place.label }))}
      onSelectOption={option => {
        const place = suggestions[Number(option.id)];
        if (!place) return;
        // Keep the picked label in the field; the empty search state matching
        // it suppresses both the dropdown and a redundant refetch.
        setQuery(place.label);
        setSearch({ query: place.label, places: [] });
        onPlaceSelect(place);
      }}
    />
  );
}
